import React, { useState, useEffect, useContext } from "react";
import { Panel } from "rsuite";
import { OrderContext } from "../helper/order-context";
import { OfferCalendar } from "../components/offer-calendar";
import { OfferCalendarDialog } from "../components/offer-calendar-dialog";
import "./offer-calendar-wrapper.less";
import prepareEventsForCalendar from "../helper/events-utilities";

export const OfferCalendarWrapper = ({
    events: calendarEvents = [],
    activeView,
    initialDate,
    timeToScroll = null,
    selectedDate,
    changeCalendarView,
    offerStartDate,
    offerEndDate
}) => {
    const { order, travelItems, wayPoints, onEditArticle } = useContext(OrderContext);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState({});
    const [open, setOpen] = useState(false);;

    const {
        onDeleteEvent,
        onDateChange,
        onOptionalChange,
        onEventDrag,
        dayStartTimes,
        offerStartTime,
        offerEndTime,
    } = useContext(OrderContext);

    useEffect(() => {
        if (!offerStartTime || !offerEndTime) return;
        const populateEvents = async () => {
            const events = await prepareEventsForCalendar({
                travelItems,
                wayPoints,
                calendarEvents,
                offerStartTime,
                offerEndTime,
            });
            setEvents(events);
        };
        populateEvents();
    }, [
        calendarEvents,
        travelItems,
        wayPoints,
        order,
        offerStartTime,
        offerEndTime,
        dayStartTimes,
    ]);

    const handleClose = () => setOpen(false);

    const handleSuccess = () => {
        onOptionalChange(selectedEvent.optional, selectedEvent);
        setOpen(false);
    };

    let eventClick = ({event}) => {
        onEditArticle(event._def.extendedProps);
    };

    if (order.modelId !== "order/order") {
        eventClick = () => {};
    }

    const eventDrop = ({ event, revert }) => {
        const isAccommodation = event.extendedProps.isAccommodation;
        if (isAccommodation && !event.end) {
            revert();
            return;
        }
        onEventDrag(event);
    };

    const eventResize = ({ event, revert }) => {
        const isAccommodation = event.extendedProps.isAccommodation;
        isAccommodation ? onEventDrag(event) : revert();
    };

    const checkboxChangeHandler = (value, checked) => {
        setSelectedEvent((previousValue) => ({
            ...previousValue,
            optional: checked,
        }));
    };

    // next piece of code is for the calendar to be able to scroll to the required time slot (8am in this case)
    // FullCalendar's scrollTime and scrollToTime are not working properly (due to 'auto' value of contentHeight property) ,
    // so I had to create this workaround
    const containerClassNames = ["calendar-wrapper", "calendar-scrollable"];
    useEffect(() => {
        const [scrollDom] = document.getElementsByClassName(
            containerClassNames.join(" ")
        );
        if (!timeToScroll) {
            scrollDom?.scrollTo(0, 0);
            return;
        }
        const scrollToTime = (time) => {
            const timeRef = scrollDom?.querySelector(
                `.fc-timegrid-slot.fc-timegrid-slot-label[data-time="${time}"]`
            );
            timeRef?.scrollIntoView(true);
            const headerHeight = scrollDom
                ?.querySelector(".fc-col-header")
                ?.getBoundingClientRect().height;
            scrollDom?.style.setProperty(
                "--all-day--sticky-top",
                `${headerHeight}px`
            );
            const allDayContainerHeight = scrollDom
                ?.querySelector(".fc-scrollgrid-section-body:first-child")
                .getBoundingClientRect().height;
            scrollDom?.scrollTo(
                0,
                scrollDom?.scrollTop - headerHeight - allDayContainerHeight
            );
        };
        const scrollFn = () => scrollToTime(timeToScroll);
        // as we're going to work with DOM elements, we need to wait for the DOM to be rendered
        const requestId = window?.requestAnimationFrame(scrollFn);

        window?.addEventListener("resize", scrollFn);
        return () => {
            window?.cancelAnimationFrame(requestId);
            window?.removeEventListener("resize", scrollFn);
        };
    }, [events, activeView, timeToScroll]);
    return (
        <Panel bordered className={containerClassNames.join(" ")}>
            <OfferCalendar
                events={events}
                eventClick={eventClick}
                eventDrop={eventDrop}
                eventResize={eventResize}
                initialDate={initialDate}
                activeView={activeView}
                onDateChange={onDateChange}
                onDeleteEvent={onDeleteEvent}
                eventStartEditable={true}
                eventDurationEditable={true}
                selectedDate={selectedDate}
                changeCalendarView={changeCalendarView}
                offerStartDate={offerStartDate}
                offerEndDate={offerEndDate}
            />
            <OfferCalendarDialog
                open={open}
                handleSuccess={handleSuccess}
                handleClose={handleClose}
                selectedEvent={selectedEvent}
                checkboxChangeHandler={checkboxChangeHandler}
            />
        </Panel>
    );
};
